import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ForcePasswordChange } from "./ForcePasswordChange";
import { useRealtime } from "../context/RealtimeContext";
import { useEffect, useRef, useState } from "react";
import { http, setPrimaryAdmin } from "../lib/api";
import { BrandBlock } from "./BrandBlock";
import { useBrand } from "../context/BrandContext";
import {
  LayoutDashboard, CalendarRange, Users, Building2, Handshake, FileCheck2, MessagesSquare,
  LogOut, UserCog, Calculator, FileText, Receipt, Settings as CogIcon, Clock, Briefcase, UserPlus, FileSignature, MapPin, ClipboardList, Activity, Map as MapIcon, Inbox, ShieldAlert, Sparkles, Menu, X, Wallet, BellRing, LifeBuoy, Building, BookOpen, Siren, BookOpenText, ClipboardCheck, KeySquare, BarChart3, Wrench
} from "lucide-react";
import { ComplianceDialogHost } from "./ComplianceDialog";
import { SiteBanDialogHost } from "./SiteBanDialog";
import { DeletionDialogHost } from "./DeletionDialog";
import { canAccess } from "../lib/sections";
import { hasPerm, isTeam } from "../lib/team";
import { ShellBanners } from "./ShellBanners";
import { ProfileDialog } from "./ProfileDialog";
import { StaffProfileDialog } from "./StaffProfile";
import { UserAvatar } from "./UserAvatar";
import { loadActivities } from "../lib/activities";
import "../xaltix-theme.css";
import "../xaltix-client.css";
import { InstallAppButton, useAppManifest } from "./InstallApp";
import { useNavDots } from "./NavDots";
import { SideNav } from "./SideNav";
import { toast } from "sonner";
import { getLang } from "../i18n";

const NAV = [
  { to: "/", section: "dashboard", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/alertes", module: "alertes", label: "Alertes", icon: BellRing },
  { to: "/clients", dot: "clients", section: "clients", label: "Clients", icon: Briefcase, group: "commercial" },
  { to: "/demandes-devis", dot: "quotes", section: "devis", module: "quote_requests", ownModule: true, label: "Demandes de devis", icon: Inbox, badge: "quotes", group: "commercial" },
  { to: "/devis", dot: "devis", section: "devis", label: "Devis", icon: FileText, group: "commercial" },
  { to: "/prestations", section: "prestations", label: "Prestations", icon: Calculator, group: "commercial" },
  { to: "/factures", section: "factures", label: "Factures", icon: Receipt, group: "commercial" },
  { to: "/planning", section: "planning", label: "Planning", icon: CalendarRange, group: "exploitation" },
  { to: "/conformite", dot: "conformite", section: "planning", module: "conformite", label: "Conformité", icon: ShieldAlert, badge: "compliance", group: "exploitation" },
  { to: "/sous-traitance", section: "agents", module: "sous_traitance", label: "Sous-traitance", icon: Handshake, group: "exploitation" },
  { to: "/sites", section: "sites", label: "Sites", icon: Building2 },
  { to: "/main-courante", section: "sites", module: "main_courante", label: "Main courante", icon: BookOpenText, group: "main_courante" },
  { to: "/postes-securite", section: "sites", module: "main_courante", label: "Postes de sécurité", icon: ShieldAlert, group: "main_courante" },
  { to: "/consignes-cles", section: "sites", module: "consignes", label: "Consignes, clés & visiteurs", icon: KeySquare, group: "main_courante" },
  { to: "/pti-rondes", section: "sites", module: "pti_rondes", label: "PTI & rondes", icon: Siren, group: "main_courante" },
  { to: "/modeles-rapports", section: "sites", module: "report_templates", label: "Modèles de rapports", icon: ClipboardCheck, group: "main_courante" },
  { to: "/pointages", dot: "pointages", section: "pointages", label: "Pointages GPS", icon: MapPin },
  { to: "/rapports", dot: "reports", section: "reports", label: "Rapports agents", icon: ClipboardList, badge: "reports" },
  { to: "/agents", section: "agents", label: "Agents", icon: Users, group: "rh" },
  { to: "/materiel", section: "materiel", label: "Matériel et équipements", icon: Wrench, group: "rh" },
  { to: "/candidats", dot: "candidats", section: "candidates", label: "Candidats", icon: UserPlus, group: "rh" },
  { to: "/heures", dot: "heures", section: "hours", label: "Heures", icon: Clock, group: "rh" },
  { to: "/prepaie", section: "prepaie", label: "Prépaie", icon: Wallet, group: "rh" },
  { to: "/documents", section: "documents", label: "Documents", icon: FileCheck2, group: "documents" },
  { to: "/modeles-documents", section: "doc_templates", label: "Modèles docs", icon: FileSignature, group: "documents" },
  { to: "/documents-agents", dot: "documents", section: "agent_docs", label: "Docs à signer", icon: FileCheck2, group: "documents" },
  { to: "/messages", dot: "messages", section: "messages", label: "Messagerie", icon: MessagesSquare, badge: "absences" },
  { to: "/parametres", section: "settings", label: "Paramètres", icon: CogIcon, group: "admin" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, adminOnly: true, group: "admin" },
  { to: "/journal", dot: "journal", label: "Journal d'activité", icon: Activity, adminOnly: true, badge: "deletions", group: "admin" },
  { to: "/carte", module: "carte", label: "Carte d'intervention", icon: MapIcon, group: "admin" },
  { to: "/contacts-site", label: "Contacts site", icon: MessagesSquare, superOnly: true },
  { to: "/contacts-xaltix", dot: "xaltix", label: "Contacts XALTIX", icon: Sparkles, superOnly: true },
  { to: "/service-client", label: "Service client", icon: LifeBuoy, badge: "support", tenantOnly: true },
];

const GROUPS = {
  commercial: { label: "Commercial", icon: Briefcase },
  exploitation: { label: "Exploitation", icon: CalendarRange },
  main_courante: { label: "Main courante", icon: BookOpenText },
  rh: { label: "RH", icon: Users },
  documents: { label: "Documents", icon: FileCheck2 },
  admin: { label: "Administration", icon: CogIcon },
  clients: { label: "Clients", icon: Building },
  contacts: { label: "Contacts", icon: Sparkles },
};

const SUPER_NAV = [
  { to: "/super/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, perm: "revenue", badge: "sa_notifs" },
  { to: "/super/entreprises", label: "Entreprises clientes", icon: Building, perm: "companies_read", badge: "limits", group: "clients" },
  { to: "/super/facturation", label: "Devis, contrats & factures", icon: Receipt, perm: ["quotes", "invoices", "revenue"], group: "clients" },
  { to: "/super/carte", label: "Carte des clients", icon: MapIcon, perm: ["companies_read", "revenue"], group: "clients" },
  { to: "/contacts-xaltix", label: "Contacts XALTIX", icon: Sparkles, perm: "leads", group: "contacts" },
  { to: "/contacts-site", label: "Contacts site", icon: MessagesSquare, group: "contacts" },
  { to: "/super/audience", label: "Audience des sites", icon: BarChart3, group: "contacts" },
  { to: "/super/candidatures", label: "Candidatures", icon: UserPlus, group: "contacts" },
  { to: "/super/service-client", label: "Service client", icon: LifeBuoy, badge: "support", perm: "support" },
  { to: "/super/equipe", label: "Équipe XALTIX", icon: Users, group: "admin" },
  { to: "/super/parametres", label: "Paramètres", icon: CogIcon, group: "admin", badge: "mail_fails" },
];

export default function Shell() {
  const { user, logout, refresh } = useAuth();
  const brand = useBrand();
  setPrimaryAdmin(user?.is_primary_admin);
  const { online, lastEvent } = useRealtime();
  const nav = useNavigate();
  const loc = useLocation();
  const dots = useNavDots(NAV, loc.pathname, lastEvent);
  const [allUsers, setAllUsers] = useState([]);
  const [unreadReports, setUnreadReports] = useState(0);
  const [newQuotes, setNewQuotes] = useState(0);
  const [pendingCompliance, setPendingCompliance] = useState(0);
  const [pendingDeletions, setPendingDeletions] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [limitAlerts, setLimitAlerts] = useState(0);

  const firstRun = useRef(true);
  const fresh = (ev) => firstRun.current || !lastEvent || lastEvent.event === ev;

  useEffect(() => {
    if (!fresh("deletion_request_changed") || !user?.is_primary_admin) return;
    http.get("/deletion-requests/pending-count").then((r) => setPendingDeletions(r.data.count)).catch(() => {});
  }, [lastEvent, user?.is_primary_admin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fresh("compliance_changed") || user?.role !== "admin") return;
    http.get("/compliance/requests/pending-count").then((r) => setPendingCompliance(r.data.count)).catch(() => {});
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastEvent?.event === "user_permissions_changed" && lastEvent.payload?.id === user?.id) refresh();
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fresh("report_changed") || !canAccess(user, "reports")) return;
    http.get("/reports/unread-count").then((r) => setUnreadReports(r.data.count)).catch(() => {});
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fresh("quote_request_changed") || !canAccess(user, "devis", "quote_requests")) return;
    http.get("/quote-requests/new-count").then((r) => setNewQuotes(r.data.count)).catch(() => {});
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isTeam(user)) http.get("/auth/users").then((r) => setAllUsers(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lastEvent?.event !== "user_changed" || isTeam(user)) return;
    http.get("/auth/users").then((r) => setAllUsers(r.data)).catch(() => {});
    if (lastEvent.payload?.id === user?.id) refresh();
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const onlineUsers = allUsers.filter((u) => online.includes(u.id));
  const sup = isTeam(user);
  useEffect(() => { if (!sup) loadActivities(true); }, [sup]);
  useEffect(() => {
    if (sup || !new URLSearchParams(loc.search).has("mes-infos")) return;
    setProfileOpen(true);
    nav(loc.pathname, { replace: true });
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  useAppManifest(sup ? { kind: "super" } : { kind: "admin", slug: user?.company?.slug || "" });
  const displayed = sup
    ? SUPER_NAV.filter((n) => (n.perm ? hasPerm(user, n.perm) : user?.is_superadmin))
    : NAV.filter((n) => !n.superOnly && (!n.adminOnly || user?.role === "admin") && canAccess(user, n.section, n.ownModule ? n.module : undefined) && !(n.module && user?.modules_off?.includes(n.module)));

  useEffect(() => {
    if (!fresh("support_changed")) return;
    http.get("/support/unread-count").then((r) => setSupportUnread(r.data.count)).catch(() => {});
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lastEvent?.event === "company_changed") refresh();
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fresh("agent_limit_attempt") || !isTeam(user) || !hasPerm(user, "companies_read")) return;
    if (lastEvent?.event === "agent_limit_attempt" && lastEvent.payload?.name) toast.warning(`${lastEvent.payload.name} a tenté de dépasser sa limite d'agents actifs`);
    http.get("/superadmin/agent-usage").then((r) => setLimitAlerts(r.data.filter((x) => x.alert?.pending > 0).length)).catch(() => {});
  }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const [absReq, setAbsReq] = useState(0);
  const absEv = lastEvent?.event === "absence_changed" ? lastEvent : null;
  useEffect(() => {
    if (isTeam(user) || !(canAccess(user, "agents") || canAccess(user, "planning"))) return;
    http.get("/absences/pending-count").then((r) => setAbsReq(r.data.can_validate ? r.data.count : 0)).catch(() => {});
    const p = absEv?.payload;
    if (p?.action === "requested" && p.id)
      http.get(`/absences/${p.id}/brief`).then(({ data: a }) => toast.info(`Nouvelle demande d'absence : ${a.agent_name} — ${a.label} du ${a.start.split("-").reverse().join("/")} au ${a.end.split("-").reverse().join("/")}`,
        { id: `abs-${a.id}`, action: { label: "Voir", onClick: () => { window.location.href = "/messages?tab=demandes"; } } })).catch(() => {});
  }, [absEv, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saNotifs, setSaNotifs] = useState(0);
  const [mailFails, setMailFails] = useState(0);
  useEffect(() => {
    if (!user?.is_superadmin) return;
    if (lastEvent?.event === "xaltix_mail_failed") toast.error("Un e-mail XALTIX a été refusé par la boîte d'envoi — voir le tableau de bord", { id: "xaltix-mail-failed" });
    http.get("/superadmin/mail-log/failed-count").then((r) => setMailFails(r.data.count)).catch(() => {});
  }, [lastEvent?.event === "xaltix_mail_failed" ? lastEvent : null, loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isTeam(user)) return undefined;
    const f = () => http.get("/superadmin/notifications").then((r) => setSaNotifs(r.data.unread)).catch(() => {});
    f(); const iv = setInterval(f, 120000); return () => clearInterval(iv);
  }, [user?.id, loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { brand.refresh(); firstRun.current = false; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sidebar = (
    <>
        <div className="flex items-center gap-3 px-4 py-6 border-b border-slate-200">
          {sup ? (
            <div data-testid="super-brand"><div className="font-display font-extrabold text-lg tracking-[0.2em]">XALTIX</div><div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{user?.is_superadmin ? "Super-administration" : "Équipe XALTIX"}</div></div>
          ) : <BrandBlock size="sm" />}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <SideNav items={displayed} groups={GROUPS} path={loc.pathname} dots={dots} onNav={() => setMenuOpen(false)}
            counts={{ absences: absReq, deletions: pendingDeletions, compliance: pendingCompliance, quotes: newQuotes, limits: limitAlerts, sa_notifs: saNotifs, mail_fails: mailFails, support: supportUnread, reports: unreadReports }} />
        </nav>

        <div className="border-t border-slate-200 p-4">
          <button type="button" onClick={() => setProfileOpen(true)} data-testid="sidebar-user-btn" className="w-full text-left flex items-center gap-3 mb-3 rounded-md hover:bg-white/10 p-1 -m-1 transition-colors" title={sup ? "Modifier mes informations" : "Ma fiche salarié (identité, diplômes)"}>
            <UserAvatar u={user} size={36} testid="sidebar-user-avatar" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] uppercase font-bold tracking-widest text-slate-500">{{ superadmin: "Super-admin", xaltix_staff: "Équipe XALTIX" }[user?.role] || user?.role}</div>
            </div>
          </button>
          {sup && profileOpen && <ProfileDialog open onClose={() => setProfileOpen(false)} />}
          {!sup && profileOpen && <StaffProfileDialog uid="me" onClose={() => setProfileOpen(false)} />}
          <button
            data-testid="logout-btn"
            onClick={async () => { const to = user?.company?.slug ? `/e/${user.company.slug}` : "/login"; await logout(); nav(to); }}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 text-white text-xs font-semibold py-2 hover:bg-slate-800 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Déconnexion
          </button>
        </div>
    </>
  );

  const offItem = NAV.find((n) => n.module && user?.modules_off?.includes(n.module) && (loc.pathname === n.to || loc.pathname.startsWith(`${n.to}/`)));
  return (
    <div className={`flex h-screen w-screen overflow-hidden bg-slate-50 ${sup ? "xt-theme" : "xc-theme"}`}>
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white" data-testid="sidebar">
        {sidebar}
      </aside>
      {/* Menu mobile */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-overlay" />
          <aside className="relative w-72 max-w-[85vw] h-full flex flex-col bg-white shadow-xl">
            <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="absolute top-3 right-3 p-2 rounded-md text-slate-500 hover:bg-slate-100" aria-label="Fermer le menu"><X className="h-5 w-5" /></button>
            {sidebar}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-3 md:px-6 gap-2">
          <button data-testid="mobile-menu-btn" onClick={() => setMenuOpen(true)} className="md:hidden p-2 -ml-1 rounded-md text-slate-700 hover:bg-slate-100" aria-label="Ouvrir le menu"><Menu className="h-5 w-5" /></button>
          <div className="hidden sm:block text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500" data-testid="header-context">
            {sup ? (user?.is_superadmin ? "Super-administration XALTIX" : "Équipe XALTIX") : user?.company?.name || "Plateforme de gestion — temps réel"}
          </div>
          {!sup && (
            <div className="ml-auto sm:ml-0 flex items-center gap-3">
              <NavLink to="/service-client" data-testid="header-support-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                <LifeBuoy className="h-3.5 w-3.5" /> Service client
              </NavLink>
              <a href={getLang() === "en" ? "/guide/XALTIX-User-Guide-EN.pdf" : "/guide/XALTIX-Guide-utilisation.pdf"} target="_blank" rel="noopener noreferrer" data-testid="header-guide-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                <BookOpen className="h-3.5 w-3.5" /> Guide
              </a>
              <span className="xc-mention" data-testid="header-xaltix-mention"><b>XALTIX</b> · par Solutions PRIME</span>
            </div>
          )}
          <div className="flex items-center gap-4" data-testid="presence-bar">
            <InstallAppButton dark className="hidden sm:inline-flex" />
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-xs font-semibold text-slate-600">{onlineUsers.length} en ligne</span>
            </div>
            <div className="flex -space-x-2">
              {onlineUsers.slice(0, 5).map((u) => (
                <div key={u.id} title={u.name} className="relative rounded-full ring-2 ring-white" data-testid={`online-user-${u.id}`}>
                  <UserAvatar u={u} size={32} />
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white"></span>
                </div>
              ))}
            </div>
          </div>
        </header>
        <ShellBanners user={user} />
        <div className="flex-1 overflow-auto app-content">
          {offItem ? <div className="p-16 text-center text-slate-600" data-testid="module-off">L&apos;option « {offItem.label} » n&apos;est pas activée pour votre entreprise. Contactez le service client XALTIX.</div>
            : <Outlet context={{ allUsers, setAllUsers }} />}
          <ComplianceDialogHost />
          <SiteBanDialogHost />
          <DeletionDialogHost />
        </div>
      </main>
      <ForcePasswordChange />
    </div>
  );
}
